import { App, Card, Modal, Spin } from "antd"
import type { BaseOptionType } from "antd/lib/select"
import React, { useCallback, useEffect, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { type ConnectedProps, connect } from "react-redux"
import { useNavigate, useParams } from "react-router"

import { FilePreviewModal } from "../../../../../../components/FilePreviewModal/FilePreviewModal.tsx"
import { projectPickerLabel } from "../../../../../../components/Shared/ProjectPickerSelect.tsx"
import { ROLE_IDS } from "../../../../../../constants/roles.ts"
import { useAppDispatch } from "../../../../../../hooks/use-app-dispatch.ts"
import { useFocusTableRow } from "../../../../../../hooks/use-focus-table-row.ts"
import { nextPageUrl } from "../../../../../../redux/shared/paged-list.ts"
import { type RootState } from "../../../../../../store.ts"
import { downloadBlob } from "../../../../../../utils/download-file.ts"
import { extractParamsFromNextUrl } from "../../../../../../utils/form/extract-params-from-next-url.ts"
import { showNotification } from "../../../../../../utils/notification/show-notification.ts"
import { serializeData } from "../../../../../../utils/utils"
import { type AddInvoiceKind } from "../../../../../Invoices/components/AddInvoiceMenu.tsx"
import { ChangeInvoiceStatusModal } from "../../../../../Invoices/components/ChangeInvoiceStatusModal.tsx"
import { InvoiceDeliveryHistoryModal } from "../../../../../Invoices/components/InvoiceDeliveryHistoryModal.tsx"
import { SendInvoiceConfirmModal } from "../../../../../Invoices/components/SendInvoiceConfirmModal.tsx"
import {
  handleInvoiceApiError,
  invoiceFormFieldErrors
} from "../../../../../Invoices/helpers/handle-invoice-api-error.ts"
import { isInvoiceGenerated } from "../../../../../Invoices/helpers/invoice-lifecycle.ts"
import { useInvoiceDocumentPreview } from "../../../../../Invoices/hooks/use-invoice-document-preview.ts"
import {
  deleteInvoice as deleteGeneratedInvoice,
  downloadInvoicePdf,
  issueInvoice,
  patchGeneratedInvoice,
  sendInvoice,
  voidInvoice
} from "../../../../../Invoices/redux/invoices.thunks.ts"
import { type InvoiceListItem } from "../../../../../Invoices/types/invoices.types.ts"
import { getInvoiceSnapshotVisibility } from "../../../../helpers/get-invoice-snapshot-visibility.tsx"
import { isArchivedProject } from "../../../../helpers/project-edit-visibility.ts"
import { selectProjectWritePermissions } from "../../../../helpers/project-permissions.selectors.ts"
import { useProjectOutletContext } from "../../../../hooks/use-project-outlet-context.tsx"
import {
  deleteInvoice,
  exportInvoices,
  fetchInvoiceSnapshot,
  fetchInvoices,
  fetchInvoicesFilterOptions,
  patchInvoice
} from "../../../../redux/project-invoices/project-invoices.thunks.ts"
import {
  projectInvoiceUpdated,
  projectSetProp
} from "../../../../redux/project.slice.ts"
import { type ProjectInvoiceFilters } from "../../../../redux/project.types.ts"
import { type ProjectInvoice } from "../../../../types/project-invoices.types.tsx"
import {
  INVOICE_TYPES,
  isConcreteInvoiceType
} from "../../../constants/invoice-statuses.ts"
import { invoiceAddPermissions } from "../../../../../Invoices/helpers/invoice-add-permissions.ts"
import { ExternalInvoiceDrawer } from "../../../../../Invoices/components/ExternalInvoiceDrawer.tsx"
import { InvoiceViewDrawer } from "../../../../../Invoices/components/InvoiceViewDrawer.tsx"
import { IssueInvoiceDrawer } from "../../../../../Invoices/components/IssueInvoiceDrawer.tsx"
import { ProjectViewInvoicesHeader } from "./ProjectViewInvoices/ProjectViewInvoicesHeader.tsx"
import ProjectViewInvoicesList from "./ProjectViewInvoices/ProjectViewInvoicesList.tsx"

const ProjectViewInvoicesSection = ({
  userId,
  userRole,
  data,
  fetching,
  filters,
  setProp,
  getFilterOptions,
  getInvoices,
  exportInvoices,
  updateInvoice,
  removeInvoice,
  refetchInvoiceSnapshot,
  projectPermissions,
  voidAllowed
}: ConnectedProps<typeof connector>) => {
  // A notification's "Review invoice" names the exact invoice; this is
  // what brings that row into view once the list has loaded it.
  useFocusTableRow("invoice")

  const { project } = useProjectOutletContext()
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const { t } = useTranslation()
  const { modal, notification } = App.useApp()
  const projectId = useParams()?.id as string
  const moduleAccess = projectPermissions?.invoices
  const moduleDocs = projectPermissions?.docs

  const invoices = data?.results || []

  const [ordering, setOrdering] = useState<string | null>(null)
  const [editableRow, setEditableRow] = useState<string>("")
  const [addKind, setAddKind] = useState<AddInvoiceKind | null>(null)
  const [editingInvoice, setEditingInvoice] = useState<ProjectInvoice | null>(
    null
  )
  const [editingExternal, setEditingExternal] = useState<ProjectInvoice | null>(
    null
  )
  const [viewingInvoice, setViewingInvoice] = useState<ProjectInvoice | null>(
    null
  )
  const [sendTarget, setSendTarget] = useState<ProjectInvoice | null>(null)
  const [sending, setSending] = useState(false)
  const [statusTarget, setStatusTarget] = useState<ProjectInvoice | null>(null)
  const [changingStatus, setChangingStatus] = useState(false)
  const [deliveryTarget, setDeliveryTarget] = useState<ProjectInvoice | null>(
    null
  )
  const [fetchingOptions, setFetchingOptions] = useState(false)
  const [filterOptions, setFilterOptions] = useState<
    Record<string, BaseOptionType[]>
  >({})

  const drawerOpen = Boolean(addKind || editingInvoice || editingExternal)
  const isLocked = !!editableRow || drawerOpen
  const { generateAllowed, externalAllowed } = invoiceAddPermissions(
    moduleAccess,
    userRole
  )
  const showInvoiceSnapshot = useMemo(
    () => getInvoiceSnapshotVisibility({ userRole, projectPermissions }),
    [userRole, projectPermissions]
  )

  const setFilters = useCallback(
    (data: Partial<ProjectInvoiceFilters>) => {
      setProp({
        key: "invoicesFilters",
        value: { ...filters, ...data }
      })
    },
    [setProp, filters]
  )

  const onAddInvoice = useCallback((kind: AddInvoiceKind) => {
    setAddKind(kind)
  }, [])

  const handleFetchInvoiceSnapshot = useCallback(() => {
    if (!showInvoiceSnapshot) return

    refetchInvoiceSnapshot(projectId)
  }, [projectId, showInvoiceSnapshot, refetchInvoiceSnapshot])

  const handleGetFilterOptions = useCallback(async () => {
    try {
      setFetchingOptions(true)
      const res = await getFilterOptions({ project_id: projectId }).unwrap()
      setFilterOptions(res)
    } catch {
      // The thunk already reported it; rethrowing only escapes to
      // window.onunhandledrejection.
      setFilterOptions({})
    } finally {
      setFetchingOptions(false)
    }
  }, [getFilterOptions, projectId])

  const handleChangePage = useCallback(
    (page: number) => {
      setFilters({ page })
    },
    [setFilters]
  )

  const handleFilterChange = useCallback(
    async (data: Partial<ProjectInvoiceFilters>) => {
      setFilters({ ...data, page: 1 })
    },
    [setFilters]
  )

  const handleGetInvoices = useCallback(async () => {
    const serializeFilters = serializeData(
      filters || {}
    ) as ProjectInvoiceFilters

    await getInvoices({
      isMore: false,
      params: { project: projectId, ordering, ...serializeFilters }
    })
  }, [projectId, filters, ordering, getInvoices])

  const nextUrl = nextPageUrl(data)
  const handleGetMoreInvoices = useCallback(async () => {
    if (!nextUrl) return

    const serializeFilters =
      extractParamsFromNextUrl<ProjectInvoiceFilters>(nextUrl)

    await getInvoices({
      isMore: true,
      params: { project: projectId, ordering, ...serializeFilters }
    })
  }, [nextUrl, getInvoices, projectId, ordering])

  // Rethrows so the row keeps the edit open and puts each field's reason under
  // its own cell; anything that is not a field error is reported here instead.
  const handleUpdateInvoice = async (data: ProjectInvoice) => {
    try {
      await updateInvoice({
        ...data,
        id: editableRow,
        silent: true,
        suppressError: true
      }).unwrap()
      notification.success({ message: t("invoices.externalDrawer.updated") })
      setEditableRow("")
      afterMutation()
    } catch (payload) {
      if (!invoiceFormFieldErrors(payload).length) reportError(payload)
      throw payload
    }
  }

  const handleExportInvoices = async () => {
    try {
      const blob = await exportInvoices(projectId)?.unwrap()
      downloadBlob(blob, `invoices-${project?.lead_name?.toLowerCase()}.xlsx`)
    } catch (e) {
      console.log("Export invoices error: ", e)
    }
  }

  const handleDeleteInvoice = async (id: string) => {
    Modal.confirm({
      title: "Delete Invoice",
      content: "Are you sure you want to delete this invoice?",
      okText: "Delete",
      okType: "danger",
      cancelText: "Cancel",
      onOk: async () => {
        try {
          await removeInvoice(id).unwrap()
          handleFetchInvoiceSnapshot()
        } catch (e) {
          console.log("Delete invoice error: ", e)
        }
      }
    })
  }

  // A blob response carries no readable status, so one generic message has to cover every failure.
  const reportPreviewError = useCallback(
    () => showNotification.error({ message: t("invoices.previewFailed") }),
    [t]
  )

  const preview = useInvoiceDocumentPreview(reportPreviewError)

  const handleDownloadPdf = async (record: ProjectInvoice) => {
    try {
      const blob = await dispatch(downloadInvoicePdf(record.id)).unwrap()
      downloadBlob(blob, `${record.invoice_number || "invoice"}.pdf`)
    } catch {
      // Errors surface via the thunk's errorSave.
    }
  }

  const reportError = (payload: unknown) =>
    handleInvoiceApiError({ payload, modal, t, navigate })

  const afterMutation = () => {
    void handleGetInvoices()
    handleFetchInvoiceSnapshot()
  }

  const handleIssue = async (record: ProjectInvoice) => {
    try {
      await dispatch(issueInvoice(record.id)).unwrap()
      notification.success({ message: t("invoices.drawer.issued") })
      afterMutation()
    } catch (payload) {
      reportError(payload)
    }
  }

  const handleVoid = (record: ProjectInvoice) => {
    modal.confirm({
      title: t("invoices.voidConfirm.title", {
        number: record.invoice_number
      }),
      content: t("invoices.voidConfirm.body"),
      okText: t("invoices.voidConfirm.ok"),
      okButtonProps: { danger: true },
      cancelText: t("invoices.voidConfirm.cancel"),
      onOk: async () => {
        try {
          await dispatch(voidInvoice(record.id)).unwrap()
          afterMutation()
        } catch (payload) {
          reportError(payload)
        }
      }
    })
  }

  const handleDeleteGenerated = (record: ProjectInvoice) => {
    modal.confirm({
      title: t("invoices.deleteConfirm.title"),
      content: t("invoices.deleteConfirm.body"),
      okText: t("invoices.deleteConfirm.ok"),
      okButtonProps: { danger: true },
      cancelText: t("invoices.deleteConfirm.cancel"),
      onOk: async () => {
        try {
          await dispatch(deleteGeneratedInvoice(record.id)).unwrap()
          afterMutation()
        } catch (payload) {
          reportError(payload)
        }
      }
    })
  }

  const handleConfirmSend = async (recipient: string) => {
    if (!sendTarget) return
    setSending(true)
    try {
      await dispatch(sendInvoice({ id: sendTarget.id, recipient })).unwrap()
      notification.success({ message: t("invoices.sent") })
      setSendTarget(null)
      afterMutation()
    } catch (payload) {
      reportError(payload)
    } finally {
      setSending(false)
    }
  }

  const handleConfirmStatus = async (status: number) => {
    if (!statusTarget) return
    setChangingStatus(true)
    try {
      const updatedInvoice = await dispatch(
        patchGeneratedInvoice({ id: statusTarget.id, status })
      ).unwrap()
      dispatch(projectInvoiceUpdated(updatedInvoice))
      notification.success({ message: t("invoices.changeStatus.success") })
      setStatusTarget(null)
      handleFetchInvoiceSnapshot()
    } catch (payload) {
      reportError(payload)
    } finally {
      setChangingStatus(false)
    }
  }

  const onSelectRow = (record: ProjectInvoice, action: string) => {
    switch (action) {
      case "view":
        return setViewingInvoice(record)
      case "preview_row":
      case "preview":
        return preview.open(record)
      case "download_pdf":
      case "download":
        return handleDownloadPdf(record)
      case "edit_row":
        return setEditableRow(record?.id)
      case "update":
        return handleUpdateInvoice(record)
      case "cancel":
        return setEditableRow("")
      case "edit_drawer":
        return isInvoiceGenerated(record)
          ? setEditingInvoice(record)
          : setEditingExternal(record)
      case "delete_row":
        return handleDeleteInvoice(record?.id)
      case "edit":
        return setEditingInvoice(record)
      case "issue":
        return handleIssue(record)
      case "delete":
        return handleDeleteGenerated(record)
      case "send":
        return setSendTarget(record)
      case "void":
        return handleVoid(record)
      case "change_status":
        return setStatusTarget(record)
      case "delivery_history":
        return setDeliveryTarget(record)
    }
  }

  useEffect(() => {
    void handleGetInvoices()
  }, [projectId, handleGetInvoices])

  useEffect(() => {
    void handleGetFilterOptions()
  }, [handleGetFilterOptions])

  return (
    <Card
      className="overflow-clip"
      classNames={{ body: "p-0", header: "p-2" }}
      data-guide-target="project-invoices-panel"
      title={
        <ProjectViewInvoicesHeader
          filters={filters}
          options={filterOptions}
          fetchingOptions={fetchingOptions}
          isEmptyList={invoices?.length === 0}
          isLocked={isLocked}
          onFilterChange={handleFilterChange}
          onExport={handleExportInvoices}
        />
      }
    >
      <ProjectViewInvoicesList
        userId={userId}
        fetching={fetching}
        projectId={projectId}
        invoices={invoices}
        type={filters?.type}
        next={data?.next}
        retryNext={data?.retryNext}
        count={data?.count || 0}
        selfAssigned={
          !!projectPermissions?.invoices?.project_invoice_self_assigned
        }
        onLoadMore={handleGetMoreInvoices}
        editableRow={editableRow}
        drawerOpen={drawerOpen}
        generateAllowed={generateAllowed}
        externalAllowed={externalAllowed}
        options={filterOptions}
        projectPermissions={projectPermissions}
        voidAllowed={voidAllowed}
        onOrderingChange={setOrdering}
        onSelectRow={onSelectRow}
        onPageChange={handleChangePage}
        onAddInvoice={onAddInvoice}
        // todo is a temporary solution and will need to be removed!
        // uploadFileAllowed={moduleDocs?.project_doc_create}
        uploadFileAllowed={
          userRole === ROLE_IDS.CUSTOMER
            ? moduleDocs?.project_doc_create
            : moduleAccess?.project_invoice_edit
        }
        deleteFileAllowed={moduleDocs?.project_doc_delete}
        deleteForeignFileAllowed={
          moduleDocs?.project_doc_delete &&
          moduleDocs?.project_doc_manage_others
        }
      />

      {generateAllowed && (
        <IssueInvoiceDrawer
          open={addKind === "generated"}
          projectId={projectId}
          projectLabel={projectPickerLabel(project)}
          onClose={() => setAddKind(null)}
          onSaved={() => {
            setAddKind(null)
            afterMutation()
          }}
        />
      )}

      <ExternalInvoiceDrawer
        open={addKind === "external" || Boolean(editingExternal)}
        projectId={projectId}
        invoice={editingExternal ?? undefined}
        defaultType={
          isConcreteInvoiceType(filters?.type)
            ? filters?.type
            : INVOICE_TYPES.RECEIVABLE
        }
        userId={userId}
        selfAssigned={
          !!projectPermissions?.invoices?.project_invoice_self_assigned
        }
        onClose={() => {
          setAddKind(null)
          setEditingExternal(null)
        }}
        onSaved={() => {
          setAddKind(null)
          setEditingExternal(null)
          afterMutation()
        }}
      />

      <IssueInvoiceDrawer
        open={Boolean(editingInvoice)}
        invoice={(editingInvoice as InvoiceListItem) ?? undefined}
        onClose={() => setEditingInvoice(null)}
        onSaved={() => {
          setEditingInvoice(null)
          afterMutation()
        }}
      />

      <InvoiceViewDrawer
        invoice={viewingInvoice}
        onClose={() => setViewingInvoice(null)}
      />

      <SendInvoiceConfirmModal
        open={Boolean(sendTarget)}
        defaultRecipient={sendTarget?.customer_email}
        loading={sending}
        onConfirm={handleConfirmSend}
        onCancel={() => setSendTarget(null)}
      />

      <ChangeInvoiceStatusModal
        open={Boolean(statusTarget)}
        currentStatus={statusTarget?.status}
        loading={changingStatus}
        onConfirm={handleConfirmStatus}
        onCancel={() => setStatusTarget(null)}
      />

      <InvoiceDeliveryHistoryModal
        open={Boolean(deliveryTarget)}
        invoiceId={deliveryTarget?.id}
        onClose={() => setDeliveryTarget(null)}
      />

      {preview.loading && <Spin fullscreen />}
      <FilePreviewModal
        visible={Boolean(preview.doc)}
        doc={preview.doc}
        onClose={preview.close}
      />
    </Card>
  )
}

const mapStateToProps = (state: RootState) => ({
  userId: state.auth.userInfo.id,
  userRole: state?.auth?.userInfo?.role?.id,
  fetching: state.project.fetchingSection,
  data: state.project.invoices,
  filters: state.project.invoicesFilters,
  projectPermissions: selectProjectWritePermissions(state),
  voidAllowed: Boolean(
    !isArchivedProject(state.project?.project) &&
    state.auth.permissions?.labels?.some(
      (label: { code?: string }) =>
        label.code === "gcc" || label.code === "gcc_admin"
    )
  )
})

const mapDispatchToProps = {
  getInvoices: fetchInvoices,
  exportInvoices,
  getFilterOptions: fetchInvoicesFilterOptions,
  updateInvoice: patchInvoice,
  removeInvoice: deleteInvoice,
  setProp: projectSetProp,
  refetchInvoiceSnapshot: fetchInvoiceSnapshot
}

const connector = connect(mapStateToProps, mapDispatchToProps)
export default connector(ProjectViewInvoicesSection)
